import { Button, Col, DatePicker, Form, InputNumber, Row, Select } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  addLeaveApplication,
  getSingleLeaveAllocation,
} from "../../redux/rtk/features/leave/leaveSlice";
import getUserFromToken from "../../utils/getUserFromToken";
import { getAllMaster } from "../../redux/rtk/features/masters/masterSlice";

dayjs.extend(utc);
dayjs.extend(timezone);

const AddLeave = ({ initialValues, onCancel }) => {
  const [loader, setLoader] = useState(false);
  const [endDate, setEndDate] = useState(null);
  const [totalDays, setTotalDays] = useState(1);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const EmployeeId = getUserFromToken();
  const [daySelectionType, setDaySelectionType] = useState("full");
  const [startDayPart, setStartDayPart] = useState(null);
  const [endDayPart, setEndDayPart] = useState(null);

  const masterData = useSelector((state) => state.masters.masters);
  const leaveDayType = masterData?.LeaveDayType;
  const allocationList = useSelector((state) => state.leave?.leave);

  const leaveTypeOptions = useMemo(() => {
    const rows = Array.isArray(allocationList)
      ? allocationList
      : allocationList
      ? [allocationList]
      : [];

    const fromAllocation = rows.reduce((acc, item) => {
      const id = item?.LeaveTypeId ?? item?.leaveTypeId;
      if (id == null) return acc;
      if (acc.some((t) => String(t.LeaveTypeId) === String(id))) return acc;
      acc.push({
        LeaveTypeId: id,
        LeaveTypeName:
          item.LeaveTypeName ||
          item.leaveType?.leaveType ||
          item.leaveType ||
          `Leave ${id}`,
      });
      return acc;
    }, []);

    if (fromAllocation.length) return fromAllocation;

    return (masterData?.LeaveTypeMaster || []).filter(
      (t) => t.DataStatus !== 0
    );
  }, [allocationList, masterData?.LeaveTypeMaster]);

  useEffect(() => {
    dispatch(getSingleLeaveAllocation(EmployeeId));
    dispatch(getAllMaster());
  }, [dispatch, EmployeeId]);

  useEffect(() => {
    if (initialValues) {
      const isCustom = initialValues.isCustom;
      setDaySelectionType(isCustom ? "custom" : "full");
      
      form.setFieldsValue({
        leaveTypeId: initialValues.leaveTypeId,
        leaveFrom: dayjs(initialValues.leaveFrom.split("|")[0]),
        leaveTo: dayjs(initialValues.leaveTo.split("|")[0]),
        totalleaveDays: initialValues.totalleaveDays,
        startDateDayTypeId: isCustom ? initialValues.startDateDayTypeId : null,
        endDateDayTypeId: isCustom ? initialValues.endDateDayTypeId : null,
      });

      if (isCustom) {
        setStartDayPart(initialValues.startDateDayTypeId);
        setEndDayPart(initialValues.endDateDayTypeId);
      }

      setTotalDays(initialValues.totalleaveDays);
      setEndDate(dayjs(initialValues.leaveTo.split("|")[0]));
    }
  }, [initialValues, form]);

useEffect(() => {
  if (daySelectionType === "custom") {
    const start = form.getFieldValue("leaveFrom");
    const end = form.getFieldValue("leaveTo");

    if (start && end) {
      const daysBetween = dayjs(end).diff(dayjs(start), "day");
      const startVal = startDayPart ? startDayPart / 4 : 0.25;
      const endVal = endDayPart ? endDayPart / 4 : 1.0;

      let total = 0;
      if (daysBetween === 0) {
        total = endVal - startVal + 0.25;
      } else {
        total = daysBetween + endVal - startVal + 0.25;
      }

      total = Math.round(total * 4) / 4;
      setTotalDays(total);
      form.setFieldsValue({ totalleaveDays: total });
    }
  } else {
    const start = form.getFieldValue("leaveFrom");
    const end = form.getFieldValue("leaveTo");
    if (start && end) {
      const diff = dayjs(end).diff(start, "day") + 1;
      setTotalDays(diff);
      form.setFieldsValue({ totalleaveDays: diff });
    }
  }
}, [daySelectionType, startDayPart, endDayPart, form]);

  const validateEndDate = (rule, value) => {
    const startDate = form.getFieldValue("leaveFrom");
    if (startDate && value && dayjs(value).isBefore(dayjs(startDate), 'day')) {
      return Promise.reject(new Error('End date cannot be earlier than start date!'));
    }
    return Promise.resolve();
  };

  const disabledEndDate = (current) => {
    const startDate = form.getFieldValue("leaveFrom");
    if (!startDate) return false;
    return current && current.isBefore(dayjs(startDate), 'day');
  };

  const handleStartDateChange = (startDate) => {
    const end = form.getFieldValue("leaveTo");
    form.setFieldsValue({ leaveFrom: startDate });
    
    if (end && dayjs(end).isBefore(dayjs(startDate), 'day')) {
      form.setFieldsValue({ leaveTo: null });
      setEndDate(null);
      setTotalDays(1);
      form.setFieldsValue({ totalleaveDays: 1 });
      toast.warning("End date has been cleared as it was earlier than the new start date");
      return;
    }

    if (daySelectionType === "full" && startDate && end) {
      const diff = dayjs(end).diff(startDate, "day") + 1;
      setTotalDays(diff);
      form.setFieldsValue({ totalleaveDays: diff });
    }
  };

  const handleEndDateChange = (endDate) => {
    const start = form.getFieldValue("leaveFrom");
    
    if (start && endDate && dayjs(endDate).isBefore(dayjs(start), 'day')) {
      toast.error("End date cannot be earlier than start date!");
      return;
    }

    form.setFieldsValue({ leaveTo: endDate });
    setEndDate(endDate);
    
    if (start && endDate) {
      if (daySelectionType === "full") {
        const diff = dayjs(endDate).diff(start, "day") + 1;
        setTotalDays(diff);
        form.setFieldsValue({ totalleaveDays: diff });
      } else {
        const startVal = startDayPart ? startDayPart / 4 : 0.25;
        const endVal = endDayPart ? endDayPart / 4 : 1.0;

        const daysBetween = dayjs(endDate).diff(dayjs(start), "day");
        let total = 0;
        if (daysBetween === 0) {
          total = endVal - startVal + 0.25;
        } else {
          total = daysBetween + endVal - startVal + 0.25;
        }
        total = Math.round(total * 4) / 4;
        setTotalDays(total);
        form.setFieldsValue({ totalleaveDays: total });
      }
    }
  };

  const onFinish = async (values) => {
    try {
      setLoader(true);
      const leaveData = {
        ...values,
        EmployeeLeaveId: initialValues ? initialValues.id : 0,
        EmployeeId: EmployeeId,
        LeaveTypeId: values.leaveTypeId,
        IsCustom: daySelectionType === "custom",
        StartDate: dayjs(values.leaveFrom).format("YYYY-MM-DD"),
        StartDateDayTypeId: daySelectionType === "custom" ? startDayPart : 1,
        EndDate: dayjs(values.leaveTo).format("YYYY-MM-DD"),
        EndDateDayTypeId: daySelectionType === "custom" ? endDayPart : 1,
        LeaveInDays: values.totalleaveDays,
        DataStatus: 1,
      };

      const resp = await dispatch(addLeaveApplication(leaveData));

      if (resp.payload.message === "success") {
        toast.success("Leave Application Created");
        dispatch(getSingleLeaveAllocation(EmployeeId)); // Refresh leave allocation
        form.resetFields();
        setEndDate(null);
        setTotalDays(1);
        setStartDayPart(null);
        setEndDayPart(null);
        setDaySelectionType("full");
        onCancel();
      } else {
        // Error messages are already handled in the thunk
      }
    } catch (error) {
      toast.error("An error occurred while submitting leave application");
    } finally {
      setLoader(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    toast.warning("Please fill all required fields");
  };

  return (
    <Fragment>
      <Row className="mr-top">
        <Col className="">
          <Form
            form={form}
            style={{ marginBottom: "40px" }}
            name="leave-form"
            labelCol={{ span: 10 }}
            wrapperCol={{ span: 14 }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            onValuesChange={() => {
              const start = form.getFieldValue("leaveFrom");
              const end = form.getFieldValue("leaveTo");
              if (daySelectionType === "custom" && start && end) {
                const startVal = startDayPart ? startDayPart / 4 : 0.25;
                const endVal = endDayPart ? endDayPart / 4 : 1.0;
                const daysBetween = dayjs(end).diff(dayjs(start), "day");
                let total = 0;
                if (daysBetween === 0) {
                  total = endVal - startVal + 0.25;
                } else {
                  total = daysBetween + endVal - startVal + 0.25;
                }
                total = Math.round(total * 4) / 4;
                setTotalDays(total);
                form.setFieldsValue({ totalleaveDays: total });
              }
            }}
          >
            <Form.Item
              name="leaveTypeId"
              label="Leave Type"
              rules={[
                { required: true, message: "Please select a leave type!" },
              ]}
            >
              <Select
                placeholder="Select leave type"
                showSearch
                optionFilterProp="children"
                loading={!masterData && !leaveTypeOptions.length}
              >
                {leaveTypeOptions.map((type) => (
                  <Select.Option
                    key={type.LeaveTypeId}
                    value={type.LeaveTypeId}
                  >
                    {type.LeaveTypeName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Start Date"
              name="leaveFrom"
              rules={[{ required: true, message: "Please select start date!" }]}
            >
              <DatePicker
                onChange={handleStartDateChange}
                format="DD-MM-YYYY"
                showTime={false}
              />
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "20px" }}
              label="End Date"
              name="leaveTo"
              rules={[
                { required: true, message: "Please select end date!" },
                { validator: validateEndDate },
              ]}
            >
              <DatePicker
                onChange={handleEndDateChange}
                format="DD-MM-YYYY"
                showTime={false}
                disabledDate={disabledEndDate}
              />
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Total Days"
              name="totalleaveDays"
            >
              <InputNumber disabled value={totalDays} />
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Leave Duration"
              name="daySelectionType"
              initialValue="full"
            >
              <Select
                value={daySelectionType}
                onChange={(value) => setDaySelectionType(value)}
              >
                <Select.Option value="full">Full Days</Select.Option>
                <Select.Option value="custom">Custom</Select.Option>
              </Select>
            </Form.Item>

            {daySelectionType === "custom" && (
              <>
                <Form.Item
                  style={{ marginBottom: "10px" }}
                  label="Start Day Type"
                  name="startDateDayTypeId"
                  rules={[
                    { required: true, message: "Please select start day type!" },
                  ]}
                >
                  <Select
                    value={startDayPart}
                    onChange={(value) => setStartDayPart(value)}
                    placeholder="Select start day type"
                  >
                    {leaveDayType?.map((type) => (
                      <Select.Option
                        key={type.LeaveDayTypeId}
                        value={type.LeaveDayTypeId}
                      >
                        {type.LeaveDayTypeName || `Quarter ${type.LeaveDayTypeId}`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "10px" }}
                  label="End Day Type"
                  name="endDateDayTypeId"
                  rules={[
                    { required: true, message: "Please select end day type!" },
                  ]}
                >
                  <Select
                    value={endDayPart}
                    onChange={(value) => setEndDayPart(value)}
                    placeholder="Select end day type"
                  >
                    {leaveDayType?.map((type) => (
                      <Select.Option
                        key={type.LeaveDayTypeId}
                        value={type.LeaveDayTypeId}
                      >
                        {type.LeaveDayTypeName || `Quarter ${type.LeaveDayTypeId}`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            <Form.Item
              style={{ marginBottom: "10px" }}
              wrapperCol={{ offset: 6, span: 12 }}
            >
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                block
                loading={loader}
                disabled={loader}
              >
                {loader
                  ? "Submitting..."
                  : initialValues
                  ? "Update Leave"
                  : "Submit Leave"}
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Fragment>
  );
};

export default AddLeave;